import React from "react";
import { jsPDF } from "jspdf";
import GuideBanner from "../components/GuideBanner";
import RelatorioDoDia from "../components/RelatorioDoDia";
import UsoIa from "../components/UsoIa";
import EfeitoIa from "../components/EfeitoIa";
import { CLIENT_STAGES, PARTNER_STAGES, PARTNER_TYPES } from "../constants";
import { fmt, fmtDate } from "../utils";

function generatePDF({ today, tasks, clientKpis, partnerKpis, overdueTasks }) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const W = 210;
  const primary = [37, 28, 89];       // #251C59
  const secondary = [0, 91, 237];     // #005BED
  const success = [70, 192, 38];      // #46C026
  const danger = [192, 38, 70];       // #C02646
  const gray = [100, 116, 139];
  const darkText = [30, 41, 59];
  let y = 0;

  // ── Header bar ──
  doc.setFillColor(...primary);
  doc.rect(0, 0, W, 32, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.text("INSTASOLUTIONS", 14, 15);
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.text("Sistema de Gestão Comercial", 14, 22);
  doc.setFontSize(10);
  doc.text(fmtDate(today), W - 14, 15, { align: "right" });
  doc.setFontSize(8);
  doc.text("Relatório Diário", W - 14, 21, { align: "right" });

  // ── Title ──
  y = 42;
  doc.setTextColor(...darkText);
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.text("Relatório Diário de Atividades", 14, y);
  y += 4;
  doc.setDrawColor(...secondary);
  doc.setLineWidth(0.8);
  doc.line(14, y, 80, y);
  y += 10;

  // ── KPI Cards ──
  const kpis = [
    { label: "Clientes Ativos", value: String(clientKpis.active), color: secondary },
    { label: "Pipeline Estimado", value: fmt(clientKpis.pipeline), color: success },
    { label: "Parceiros Ativos", value: String(partnerKpis.active), color: [124, 58, 237] },
    { label: "Tarefas Pendentes", value: String(tasks.filter(t => !t.done).length), color: danger },
  ];
  const cardW = (W - 28 - 12) / 4;
  kpis.forEach((k, i) => {
    const x = 14 + i * (cardW + 4);
    doc.setFillColor(248, 250, 252);
    doc.roundedRect(x, y, cardW, 22, 2, 2, "F");
    doc.setDrawColor(...k.color);
    doc.setLineWidth(0.6);
    doc.line(x, y, x, y + 22);
    doc.setTextColor(...k.color);
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text(k.value, x + 4, y + 10);
    doc.setTextColor(...gray);
    doc.setFontSize(7);
    doc.setFont("helvetica", "normal");
    doc.text(k.label, x + 4, y + 17);
  });
  y += 30;

  // ── Section: Clientes ──
  doc.setFillColor(...secondary);
  doc.roundedRect(14, y, W - 28, 7, 1, 1, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.text("FRENTE 1 — PIPELINE DE CLIENTES", 18, y + 5);
  y += 12;

  // Table header
  doc.setFillColor(241, 245, 249);
  doc.rect(14, y, W - 28, 7, "F");
  doc.setTextColor(...darkText);
  doc.setFontSize(8);
  doc.setFont("helvetica", "bold");
  doc.text("Etapa", 18, y + 5);
  doc.text("Qtd", W - 34, y + 5, { align: "right" });
  y += 7;

  CLIENT_STAGES.filter(s => !["descartado"].includes(s.id)).forEach(s => {
    const count = clientKpis.byStage[s.id] || 0;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    // Color dot
    const hex = s.color;
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    doc.setFillColor(r, g, b);
    doc.circle(20, y + 3, 1.5, "F");
    doc.setTextColor(...darkText);
    doc.text(s.label, 24, y + 4.5);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(count > 0 ? [30, 41, 59] : [203, 213, 225]);
    doc.text(String(count), W - 34, y + 4.5, { align: "right" });
    // separator
    doc.setDrawColor(241, 245, 249);
    doc.setLineWidth(0.2);
    doc.line(14, y + 7, W - 14, y + 7);
    y += 8;
  });

  // Total pipeline
  doc.setDrawColor(...secondary);
  doc.setLineWidth(0.5);
  doc.line(14, y, W - 14, y);
  y += 2;
  doc.setTextColor(...darkText);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.text("Pipeline Total:", 18, y + 5);
  doc.setTextColor(...success);
  doc.text(fmt(clientKpis.pipeline), W - 34, y + 5, { align: "right" });
  y += 12;

  // ── Section: Parceiros ──
  doc.setFillColor(124, 58, 237);
  doc.roundedRect(14, y, W - 28, 7, 1, 1, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.text("FRENTE 2 — REDE DE PARCEIROS", 18, y + 5);
  y += 12;

  // Table header
  doc.setFillColor(241, 245, 249);
  doc.rect(14, y, (W - 28) / 2 - 2, 7, "F");
  doc.setTextColor(...darkText);
  doc.setFontSize(8);
  doc.setFont("helvetica", "bold");
  doc.text("Etapa", 18, y + 5);
  doc.text("Qtd", 14 + (W - 28) / 2 - 10, y + 5, { align: "right" });

  const rightX = 14 + (W - 28) / 2 + 2;
  doc.setFillColor(241, 245, 249);
  doc.rect(rightX, y, (W - 28) / 2 - 2, 7, "F");
  doc.text("Tipo", rightX + 4, y + 5);
  doc.text("Qtd", W - 18, y + 5, { align: "right" });
  y += 8;

  const maxRows = Math.max(PARTNER_STAGES.length, PARTNER_TYPES.length);
  for (let i = 0; i < maxRows; i++) {
    const stage = PARTNER_STAGES[i];
    const ptype = PARTNER_TYPES[i];
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    if (stage) {
      const count = partnerKpis.byStage[stage.id] || 0;
      const hex = stage.color;
      doc.setFillColor(parseInt(hex.slice(1, 3), 16), parseInt(hex.slice(3, 5), 16), parseInt(hex.slice(5, 7), 16));
      doc.circle(20, y + 3, 1.5, "F");
      doc.setTextColor(...darkText);
      doc.text(stage.label, 24, y + 4.5);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(count > 0 ? darkText : [203, 213, 225]);
      doc.text(String(count), 14 + (W - 28) / 2 - 10, y + 4.5, { align: "right" });
    }
    if (ptype) {
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...darkText);
      doc.text(`${ptype.label}`, rightX + 4, y + 4.5);
      doc.setFont("helvetica", "bold");
      const c = partnerKpis.byType[ptype.id] || 0;
      doc.setTextColor(c > 0 ? darkText : [203, 213, 225]);
      doc.text(String(c), W - 18, y + 4.5, { align: "right" });
    }
    y += 8;
  }
  y += 4;

  // ── Section: Tarefas ──
  doc.setFillColor(...primary);
  doc.roundedRect(14, y, W - 28, 7, 1, 1, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.text("TAREFAS DO DIA", 18, y + 5);
  y += 12;

  const doneTasks = tasks.filter(t => t.done);
  const pendingTasks = tasks.filter(t => !t.done);

  const taskRows = [
    { label: "Tarefas concluídas", value: doneTasks.length, color: success },
    { label: "Tarefas pendentes", value: pendingTasks.length, color: [245, 158, 11] },
    { label: "Follow-ups atrasados", value: overdueTasks.length, color: danger },
    { label: "Ganhos (Clientes)", value: clientKpis.won || 0, color: success },
  ];
  taskRows.forEach(row => {
    doc.setTextColor(...darkText);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text(row.label, 18, y + 4);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...row.color);
    doc.text(String(row.value), W - 34, y + 4, { align: "right" });
    doc.setDrawColor(241, 245, 249);
    doc.setLineWidth(0.2);
    doc.line(14, y + 7, W - 14, y + 7);
    y += 9;
  });

  // ── Overdue tasks list ──
  if (overdueTasks.length > 0) {
    y += 4;
    doc.setTextColor(...danger);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text("Tarefas Atrasadas:", 18, y);
    y += 5;
    overdueTasks.slice(0, 10).forEach(t => {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(...darkText);
      doc.text(`• ${t.title}`, 22, y);
      doc.setTextColor(...gray);
      doc.text(fmtDate(t.dueDate), W - 34, y, { align: "right" });
      y += 5;
    });
    if (overdueTasks.length > 10) {
      doc.setTextColor(...gray);
      doc.setFontSize(7);
      doc.text(`... e mais ${overdueTasks.length - 10} tarefa(s)`, 22, y);
      y += 5;
    }
  }

  // ── Footer ──
  const footerY = 285;
  doc.setDrawColor(...primary);
  doc.setLineWidth(0.5);
  doc.line(14, footerY, W - 14, footerY);
  doc.setTextColor(...gray);
  doc.setFontSize(7);
  doc.setFont("helvetica", "normal");
  doc.text("InstaSolutions — Sistema de Gestão Comercial", 14, footerY + 4);
  doc.text(`Gerado em ${fmtDate(today)} às ${new Date().toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}`, W - 14, footerY + 4, { align: "right" });
  doc.text("Documento confidencial — Uso interno", W / 2, footerY + 8, { align: "center" });

  doc.save(`relatorio-diario-${today}.pdf`);
}

export default function Reports({ today, tasks, clientKpis, partnerKpis, overdueTasks, partners = [], credenciamentos = [], satisfaction = [], metas = [], role = "comercial" }) {
  const [copied, setCopied] = React.useState(false);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <GuideBanner
        title="Relatório Diário — Envie para a Gerente de Operações"
        text="O bloco de cima resume o DIA: o que aconteceu, quem fez e o que ficou para trás. Copie e mande no grupo no fim do expediente. Abaixo fica o retrato do pipeline inteiro, que muda mais devagar."
      />

      <RelatorioDoDia partners={partners} credenciamentos={credenciamentos} satisfaction={satisfaction} metas={metas} dia={today} />

      <EfeitoIa partners={partners} role={role} />
      <UsoIa partners={partners} hoje={today} role={role} />
      <div style={{ background: "#fff", borderRadius: 10, padding: "20px 22px", border: "1px solid #e5e7eb" }}>
        <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>Resumo do Pipeline — {fmtDate(today)}</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16 }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#2563eb", marginBottom: 10 }}>Frente 1 — Clientes</div>
            {CLIENT_STAGES.filter((s) => !["descartado"].includes(s.id)).map((s) => {
              const count = clientKpis.byStage[s.id] || 0;
              return count > 0 ? (
                <div key={s.id} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: 12 }}>
                  <span style={{ color: "#475569" }}>
                    <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 2, background: s.color, marginRight: 6, verticalAlign: "middle" }} />
                    {s.label}
                  </span>
                  <span style={{ fontWeight: 700 }}>{count}</span>
                </div>
              ) : null;
            })}
            <div style={{ borderTop: "1px solid #e5e7eb", marginTop: 8, paddingTop: 8, display: "flex", justifyContent: "space-between", fontSize: 13, fontWeight: 700 }}>
              <span>Pipeline Total:</span>
              <span style={{ color: "#0d9488" }}>{fmt(clientKpis.pipeline)}</span>
            </div>
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#7c3aed", marginBottom: 10 }}>Frente 2 — Parceiros</div>
            {PARTNER_STAGES.map((s) => {
              const count = partnerKpis.byStage[s.id] || 0;
              return count > 0 ? (
                <div key={s.id} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: 12 }}>
                  <span style={{ color: "#475569" }}>
                    <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 2, background: s.color, marginRight: 6, verticalAlign: "middle" }} />
                    {s.label}
                  </span>
                  <span style={{ fontWeight: 700 }}>{count}</span>
                </div>
              ) : null;
            })}
            <div style={{ borderTop: "1px solid #e5e7eb", marginTop: 8, paddingTop: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 600 }}>Cobertura por tipo:</div>
              {PARTNER_TYPES.map((t) => (
                <div key={t.id} style={{ display: "flex", justifyContent: "space-between", padding: "2px 0", fontSize: 11 }}>
                  <span>{t.label}</span>
                  <span style={{ fontWeight: 600 }}>{partnerKpis.byType[t.id] || 0}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Resumo copiável */}
        <div style={{ marginTop: 16, padding: "14px 16px", borderRadius: 8, background: "#f0fdfa", border: "1px solid #99f6e4" }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#0f4f46", marginBottom: 6 }}>Resumo para enviar à Gerente de Operações:</div>
          <div style={{ fontSize: 12, color: "#115e59", lineHeight: 1.6, fontFamily: "monospace" }}>
            Relatório {fmtDate(today)}<br />
            — Clientes ativos no pipeline: {clientKpis.active}<br />
            — Pipeline estimado: {fmt(clientKpis.pipeline)}<br />
            — Parceiros ativos na rede: {partnerKpis.active}<br />
            — Parceiros em credenciamento: {partnerKpis.inProgress}<br />
            — Tarefas concluídas hoje: {tasks.filter((t) => t.done).length}<br />
            — Tarefas pendentes: {tasks.filter((t) => !t.done).length}<br />
            — Follow-ups atrasados: {overdueTasks.length}
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
        <button
          onClick={() => {
            const text = `Relatório ${fmtDate(today)}\n— Clientes ativos no pipeline: ${clientKpis.active}\n— Pipeline estimado: ${fmt(clientKpis.pipeline)}\n— Parceiros ativos na rede: ${partnerKpis.active}\n— Parceiros em credenciamento: ${partnerKpis.inProgress}\n— Tarefas concluídas hoje: ${tasks.filter((t) => t.done).length}\n— Tarefas pendentes: ${tasks.filter((t) => !t.done).length}\n— Follow-ups atrasados: ${overdueTasks.length}`;
            if (navigator.clipboard && navigator.clipboard.writeText) {
              navigator.clipboard.writeText(text).then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
              }).catch(() => {
                fallbackCopy(text);
              });
            } else {
              fallbackCopy(text);
            }
            function fallbackCopy(t) {
              const ta = document.createElement("textarea");
              ta.value = t;
              ta.style.position = "fixed";
              ta.style.opacity = "0";
              document.body.appendChild(ta);
              ta.select();
              document.execCommand("copy");
              document.body.removeChild(ta);
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            }
          }}
          style={{
            padding: "8px 16px", borderRadius: 7, border: "none",
            background: copied ? "#059669" : "#0d9488", color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer",
            transition: "background 0.2s",
          }}
        >
          {copied ? "Copiado!" : "Copiar Resumo"}
        </button>
        <button
          onClick={() => generatePDF({ today, tasks, clientKpis, partnerKpis, overdueTasks })}
          style={{
            padding: "8px 16px", borderRadius: 7, border: "none",
            background: "linear-gradient(135deg, #251C59, #005BED)", color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer",
          }}
        >
          Baixar PDF
        </button>
        </div>
      </div>
    </div>
  );
}
